// =============================================================================
// ÍNDICE PRINCIPAL DE TOKENS - SISTEMA DE DISEÑO TADMIN
// =============================================================================
// Exportación centralizada de todos los tokens del sistema de diseño
// para facilitar el uso y mantenimiento

// Importar tokens base
import colors from './colors.js';
import typography from './typography.js';
import spacing from './spacing.js';
import shadows from './shadows.js';
import borders from './borders.js';
import formatters from './formatters.js';

// Importar tokens de componentes organizados
import { buttonTokens, inputTokens, cardTokens } from './components.js';
import { sidebarTokens, breadcrumbTokens, tabsTokens } from './navigation.js';
import { dashboardTokens, loadingSpinnerTokens, errorMessageTokens, layoutTokens } from './dashboard.js';
// Importar tokens de formularios
import { selectTokens, formTokens, fieldTokens, fileUploadTokens } from './forms.js';
import { paymentTokens } from './payments.js';

// =============================================================================
// TOKENS SHADCN/UI COMPATIBLES
// =============================================================================

// Mapeo de tokens a variables CSS de shadcn/ui
export const shadcnTokens = {
  colors: {
    // Colores base de shadcn/ui
    background: 'hsl(var(--background))',
    foreground: 'hsl(var(--foreground))',
    card: 'hsl(var(--card))',
    cardForeground: 'hsl(var(--card-foreground))',
    popover: 'hsl(var(--popover))',
    popoverForeground: 'hsl(var(--popover-foreground))',
    primary: 'hsl(var(--primary))',
    primaryForeground: 'hsl(var(--primary-foreground))',
    secondary: 'hsl(var(--secondary))',
    secondaryForeground: 'hsl(var(--secondary-foreground))',
    muted: 'hsl(var(--muted))',
    mutedForeground: 'hsl(var(--muted-foreground))',
    accent: 'hsl(var(--accent))',
    accentForeground: 'hsl(var(--accent-foreground))',
    destructive: 'hsl(var(--destructive))',
    destructiveForeground: 'hsl(var(--destructive-foreground))',
    border: 'hsl(var(--border))',
    input: 'hsl(var(--input))',
    ring: 'hsl(var(--ring))',
    // Colores de sidebar
    sidebar: 'hsl(var(--sidebar-background))',
    sidebarForeground: 'hsl(var(--sidebar-foreground))',
    sidebarPrimary: 'hsl(var(--sidebar-primary))',
    sidebarPrimaryForeground: 'hsl(var(--sidebar-primary-foreground))',
    sidebarAccent: 'hsl(var(--sidebar-accent))',
    sidebarAccentForeground: 'hsl(var(--sidebar-accent-foreground))',
    sidebarBorder: 'hsl(var(--sidebar-border))',
    sidebarRing: 'hsl(var(--sidebar-ring))',
  },
  radius: {
    default: 'var(--radius)',
    sm: 'calc(var(--radius) - 2px)',
    md: 'calc(var(--radius) - 4px)',
    lg: 'var(--radius)',
  },
};

// =============================================================================
// TOKENS YOOL — ALIASES PARA COMPONENTES UI
// =============================================================================
// Exportados como named exports para que switch.jsx, tabs.jsx y futuros
// componentes puedan importar: import { yoolColors, yoolBorders, yoolShadows }

/**
 * yoolColors — espejo JS de los tokens del Yool Design System.
 *
 * NO ES FUENTE DE VERDAD. La fuente es
 * `apps-design-system/project/colors_and_type.css` (Claude Design 1a4bd3e4),
 * que llega a la app vía `src/styles/ds/colors_and_type.css` (`npm run ds:sync`).
 *
 * Este espejo ya coincide token por token con el DS, incluidos los `states`
 * (successBg/warningBg/errorBg/infoBg = los `--badge-*-bg`). Es el espejo que
 * quedó bien; `yoolBrand` en ./colors.js era el que tenía tintes propios y se
 * alineó a éste. Si hace falta un color nuevo, se agrega en el DS y se
 * sincroniza — no se inventa aquí.
 *
 * @see docs/design-system-unico.md
 */
export const yoolColors = {
  /** Negro Apple · tipografía principal */
  black:   '#1A1A1A',
  /** Verde Petróleo · CTA exclusivo · solo botones de acción primaria */
  cta:     '#007D6E',
  /** Cyan · narrativa digital · loading · tooltips */
  cyan:    '#06B6D4',
  /** Navy · headers dark · secciones estructurales oscuras */
  navy:    '#0E2640',
  /** Gris oscuro · texto secundario */
  gray1:   '#54585A',
  /** Gris muted · labels · placeholders · metadata */
  muted:   '#8D8D8D',
  /** Verde Éxito · confirmaciones · checks · badge activo */
  success: '#158B5C',
  /** Rojo Error · errores · validaciones */
  error:   '#ED4068',
  /** Gris claro · ghost buttons · fondos alternativos */
  gray:    '#EBEBEB',
  /** Borde universal · separadores */
  border:  '#E0E0E0',
  /** Fondo de página */
  pageBg:  '#F7F7F7',
  /** Blanco · cards · superficies */
  white:   '#FFFFFF',

  // Estados semánticos (fondos y textos de badges)
  states: {
    successBg:    '#D4EEE0',
    warningBg:    '#FFEEE8',
    errorBg:      '#FFDDE5',
    infoBg:       '#E8F0FF',
    successText:  '#0A5C36',
    warningText:  '#D35400',
    errorText:    '#C0004A',
    infoText:     '#1D4ED8',
  },
};

/**
 * Tokens de bordes y radios Yool v5.2
 */
export const yoolBorders = {
  radius: {
    sm:   '8px',
    md:   '10px',
    lg:   '12px',
    xl:   '16px',
    xxl:  '20px',
    full: '100px',   // para switches, badges, pills
  },
  width:  '1px',
  color:  '#E0E0E0',
};

/**
 * Tokens de sombras Yool v5.2
 * Flat-first: usar con moderación
 */
export const yoolShadows = {
  sm: '0 1px 3px rgba(0,0,0,0.06)',
  md: '0 4px 6px rgba(0,0,0,0.07)',
  lg: '0 10px 15px rgba(0,0,0,0.08)',
};

// =============================================================================
// TOKENS PRINCIPALES
// =============================================================================

// Exportar tokens individuales
export { colors, typography, spacing, shadows, borders, formatters };

// Exportar tokens de componentes
export { buttonTokens, inputTokens, cardTokens };
export { sidebarTokens, breadcrumbTokens, tabsTokens };
export { dashboardTokens, loadingSpinnerTokens, errorMessageTokens, layoutTokens };
export { selectTokens, formTokens, fieldTokens, fileUploadTokens };
export { paymentTokens };

// Exportar tokens específicos más utilizados para acceso directo
// Exportar tipografía
const typographyData = typography || {};
export const fontFamily = typographyData.fontFamilies || {};
export const fontSize = typographyData.fontSizes || {};
export const fontWeight = typographyData.fontWeights || {};
export const lineHeight = typographyData.lineHeights || {};

// Sombras individuales
export const shadowNone = (shadows.default && shadows.default.base && shadows.default.base.none) || (shadows.base && shadows.base.none) || 'none';
export const shadowXs = (shadows.default && shadows.default.base && shadows.default.base.xs) || (shadows.base && shadows.base.xs) || '0 1px 2px 0 rgba(0, 0, 0, 0.05)';
export const shadowSm = (shadows.default && shadows.default.base && shadows.default.base.sm) || (shadows.base && shadows.base.sm) || '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)';
export const shadowMd = (shadows.default && shadows.default.base && shadows.default.base.md) || (shadows.base && shadows.base.md) || '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)';
export const shadowLg = (shadows.default && shadows.default.base && shadows.default.base.lg) || (shadows.base && shadows.base.lg) || '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)';
export const shadowXl = (shadows.default && shadows.default.base && shadows.default.base.xl) || (shadows.base && shadows.base.xl) || '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)';

// Radios de borde individuales
export const radiusNone = (borders.default && borders.default.radius && borders.default.radius.none) || (borders.radius && borders.radius.none) || '0';
export const radiusSm = (borders.default && borders.default.radius && borders.default.radius.sm) || (borders.radius && borders.radius.sm) || '4px';
export const radiusMd = (borders.default && borders.default.radius && borders.default.radius.md) || (borders.radius && borders.radius.md) || '6px';
export const radiusLg = (borders.default && borders.default.radius && borders.default.radius.lg) || (borders.radius && borders.radius.lg) || '8px';
export const radiusFull = (borders.default && borders.default.radius && borders.default.radius.full) || (borders.radius && borders.radius.full) || '9999px';

// =============================================================================
// TOKENS DE COMPONENTES MEJORADOS PARA SHADCN/UI
// =============================================================================



// Tokens mejorados de navegación para shadcn/ui
export const navigationTokens = {
  sidebar: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'hsl(var(--sidebar-background))',
      color: 'hsl(var(--sidebar-foreground))',
      border: '1px solid hsl(var(--sidebar-border))',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      padding: '1rem',
      borderBottom: '1px solid hsl(var(--sidebar-border))',
    },
    content: {
      flex: '1',
      overflow: 'auto',
      padding: '0.5rem',
    },
    menu: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.75rem',
      borderRadius: 'var(--radius)',
      padding: '0.5rem 0.75rem',
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      color: 'hsl(var(--sidebar-foreground))',
      textDecoration: 'none',
      transition: 'colors 0.2s',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'hsl(var(--sidebar-accent))',
        color: 'hsl(var(--sidebar-accent-foreground))',
      },
      '&[data-active="true"]': {
        backgroundColor: 'hsl(var(--sidebar-primary))',
        color: 'hsl(var(--sidebar-primary-foreground))',
      },
    },
    trigger: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '0.5rem 0.75rem',
      borderRadius: 'var(--radius)',
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      color: 'hsl(var(--sidebar-foreground))',
      backgroundColor: 'transparent',
      border: 'none',
      cursor: 'pointer',
      transition: 'colors 0.2s',
      '&:hover': {
        backgroundColor: 'hsl(var(--sidebar-accent))',
        color: 'hsl(var(--sidebar-accent-foreground))',
      },
      '&[data-state="open"]': {
        backgroundColor: 'hsl(var(--sidebar-accent))',
        color: 'hsl(var(--sidebar-accent-foreground))',
      },
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      marginTop: '0.5rem',
      paddingLeft: '1rem',
    },
    groupLabel: {
      fontSize: fontSize.xs,
      fontWeight: fontWeight.medium,
      color: 'hsl(var(--muted-foreground))',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      padding: '0.5rem 0.75rem 0.25rem',
    },
  },
  breadcrumb: {
    container: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      fontSize: fontSize.sm,
      color: 'hsl(var(--muted-foreground))',
    },
    item: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      color: 'hsl(var(--foreground))',
      textDecoration: 'none',
      transition: 'color 0.2s',
      '&:hover': {
        color: 'hsl(var(--primary))',
      },
      '&[data-current="true"]': {
        color: 'hsl(var(--muted-foreground))',
        pointerEvents: 'none',
      },
    },
    separator: {
      color: 'hsl(var(--muted-foreground))',
      fontSize: fontSize.sm,
    },
  },
};

// =============================================================================
// TOKENS DE NAVEGACIÓN MEJORADOS PARA SHADCN/UI
// =============================================================================


// Tokens de pagos mejorados para shadcn/ui
export const enhancedPaymentTokens = {
  paymentCard: {
    container: {
      padding: '1.5rem',
      transition: 'box-shadow 0.2s',
      '&:hover': {
        boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
      },
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '1rem',
    },
    content: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    footer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: '1px solid',
      borderTopColor: 'hsl(var(--border))',
    },
  },
  statusBadge: {
    base: {
      display: 'inline-flex',
      alignItems: 'center',
      borderRadius: 'calc(var(--radius) - 2px)',
      paddingLeft: '0.625rem',
      paddingRight: '0.625rem',
      paddingTop: '0.125rem',
      paddingBottom: '0.125rem',
      fontSize: fontSize.xs,
      fontWeight: fontWeight.medium,
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },
    variants: {
      pendiente: {
        backgroundColor: 'hsl(var(--payment-pendiente))',
        color: 'hsl(var(--payment-pendiente-foreground))',
      },
      procesando: {
        backgroundColor: 'hsl(var(--payment-procesando))',
        color: 'hsl(var(--payment-procesando-foreground))',
      },
      pagado: {
        backgroundColor: 'hsl(var(--payment-pagado))',
        color: 'hsl(var(--payment-pagado-foreground))',
      },
      expirado: {
        backgroundColor: 'hsl(var(--payment-expirado))',
        color: 'hsl(var(--payment-expirado-foreground))',
      },
      cancelado: {
        backgroundColor: 'hsl(var(--payment-cancelado))',
        color: 'hsl(var(--payment-cancelado-foreground))',
      },
    },
  },
  amountDisplay: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '0.25rem',
    },
    primary: {
      fontSize: fontSize.xl,
      fontWeight: fontWeight.bold,
      color: 'hsl(var(--foreground))',
      lineHeight: '1.25',
    },
    secondary: {
      fontSize: fontSize.sm,
      color: 'hsl(var(--muted-foreground))',
    },
    currency: {
      fontSize: fontSize.sm,
      fontWeight: fontWeight.medium,
      color: 'hsl(var(--muted-foreground))',
    },
  },
};

// =============================================================================
// UTILIDADES Y FUNCIONES HELPER
// =============================================================================

export const getTokensByTheme = (theme = 'light') => {
  return {
    colors: colors[theme] || colors.light,
    typography,
    spacing,
    shadows,
    borders,
  };
};

export const createComponentVariant = (baseStyles, variantStyles) => {
  return {
    ...baseStyles,
    ...variantStyles,
  };
};

export const createResponsiveStyles = (mobileStyles, desktopStyles) => {
  return {
    '@media (max-width: 768px)': mobileStyles,
    '@media (min-width: 769px)': desktopStyles,
  };
};

export const createTransition = (properties = ['all'], duration = '0.2s', easing = 'ease-in-out') => {
  return `${properties.join(', ')} ${duration} ${easing}`;
};

// =============================================================================
// EXPORTACIÓN PRINCIPAL
// =============================================================================

const designTokens = {
  // Tokens base
  colors,
  typography,
  spacing,
  shadows,
  borders,
  formatters,
  
  // Tokens de shadcn/ui
  shadcn: shadcnTokens,
  
  // Tokens de componentes
  button: buttonTokens,
  input: inputTokens,
  card: cardTokens,
  sidebar: sidebarTokens,
  breadcrumb: breadcrumbTokens,
  tabs: tabsTokens,
  dashboard: dashboardTokens,
  loadingSpinner: loadingSpinnerTokens,
  errorMessage: errorMessageTokens,
  layout: layoutTokens,
  select: selectTokens,
  form: formTokens,
  field: fieldTokens,
  fileUpload: fileUploadTokens,
  payment: paymentTokens,
  
  // Tokens de utilidades mejorados
  // (sectionCards, component y enhancedDashboard se eliminaron: eran
  //  style-objects @deprecated con gradientes y sombras fuera de marca, sin
  //  ningún consumidor. Para estilar se usa el DS, no style-objects.)
  navigation: navigationTokens,
  enhancedPayment: enhancedPaymentTokens,
  
  // Utilidades
  utils: {
    getTokensByTheme,
    createComponentVariant,
    createResponsiveStyles,
    createTransition,
  },
};

export default designTokens;